import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Github, Linkedin } from 'lucide-react';
import { personalInfo } from '../mockData';
import { motion } from 'framer-motion';
import TypewriterText from './TypewriterText';

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
      {/* Star particles background */}
      <div className="absolute inset-0 stars-background"></div>

      <div className="container mx-auto px-6 py-12 relative z-10">
        <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-10rem)]">
          {/* Left Content */}
          <motion.div
            className="space-y-6 flex flex-col justify-center text-center lg:text-left pt-10 lg:pt-0"
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-bold"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="text-cyan-400">{personalInfo.greeting}</span>
              <br />
              <TypewriterText
                text={personalInfo.name}
                delay={100}
                className="text-white"
              />
            </motion.h1>
            <motion.h2
              className="text-2xl md:text-3xl font-semibold gradient-text"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {personalInfo.title}
            </motion.h2>
            <motion.p
              className="text-xl md:text-2xl text-cyan-300 font-medium"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              {personalInfo.subtitle}
            </motion.p>
            <motion.p
              className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              {personalInfo.description}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-wrap gap-4 pt-4 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <Button
                onClick={() => scrollToSection('projects')}
                className="bg-cyan-400 hover:bg-cyan-500 text-black font-semibold px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 magnetic glow-on-hover"
              >
                View My Work
              </Button>
              <Button
                variant="outline"
                onClick={() => scrollToSection('contact')}
                className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-black font-semibold px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 magnetic"
              >
                Contact Me
              </Button>
            </motion.div>

            {/* Social Links */}
            <motion.div
              className="flex gap-4 pt-4 justify-center lg:justify-start"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-cyan-400 transition-all duration-300 hover:scale-125"
              >
                <Linkedin size={28} />
              </a>
              <a
                href={personalInfo.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-cyan-400 transition-all duration-300 hover:scale-125"
              >
                <Github size={28} />
              </a>
            </motion.div>
          </motion.div>

          {/* Right Content - 3D Character */}
          <motion.div
            className="flex items-center justify-center lg:justify-end h-full"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative w-full max-w-sm md:max-w-xl lg:max-w-2xl">
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-cyan-600/20 to-blue-600/20 rounded-full blur-3xl"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.img
                src="/assets/hero-character.png"
                alt="K R Pavan Tej - DevOps & Cloud Engineer"
                width={600}
                height={600}
                className="relative z-10 w-full h-auto max-h-[400px] lg:max-h-[600px] object-contain"
                style={{ filter: 'drop-shadow(0 0 50px rgba(6, 182, 212, 0.4))' }}
                animate={{
                  y: [0, -20, 0]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
